import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import atlas, {data, layer, MapMouseEvent, source} from "azure-maps-control";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzurePieClusterBubbleLayer} from "@esWebAPI/es/map/layer/azure/AzurePieClusterBubbleLayer";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {AzurePieClusterLabelLayer} from "@esWebAPI/es/map/layer/azure/AzurePieClusterLabelLayer";
import _, {isNaN} from "lodash";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";
import {isMouseoverLabellingEnabled, isPieClusteringOn} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {ColorThematic} from "@esWebAPI/es/model/Thematic";
import {Chart, registerables} from 'chart.js';
import {ChartConfiguration} from "chart.js/dist/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {AzureMouseoverLabelHandler} from "@esWebAPI/es/map/handler/AzureMouseoverLabelHandler";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";

Chart.register(...registerables);
Chart.register(ChartDataLabels);

const DELAY_TIMER = 500;

export class PieClusterLayer extends MapLayer {

    private readonly _datasource: source.Source;

    private readonly _clusterLayer: AzurePieClusterBubbleLayer;
    private readonly _clusterLabelLayer: AzurePieClusterLabelLayer;

    private _pieChartWidget: Chart | undefined;
    private _colorThematic: ColorThematic|undefined;


    private hoverTimeoutId: number|undefined;
    private currentClusterId: number|undefined;

    private handleSuperclusterExpired = _.debounce(this.reloadSupercluster.bind(this), 500);

    constructor(map: atlas.Map, layerView: ILayerView, layerInfo: LayerInfo, datasourceManager: DatasourceManager,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel, datasource: source.Source, colorThematic: ColorThematic | undefined) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        const colorStyler = stylerFactory.getColorStyler(layerView);

        this._colorThematic = colorThematic;
        this._clusterLayer = new AzurePieClusterBubbleLayer(map, layerView, layerInfo, datasourceManager, colorStyler);
        this._clusterLabelLayer = new AzurePieClusterLabelLayer(map, layerView, layerInfo, datasourceManager, colorStyler);

        this._datasource = datasource;

        this.createLayers().then(() => {
            this.setupNumberClusterLayer();

            // only create mouseover handler for clusters when there's no color thematic,
            // when color thematic exists, setupNumberClusterLayer handles it with pie charts
            const isMouseoverEnabled = isMouseoverLabellingEnabled(this.layerView);
            const isColorThematicEnabled = !!layerView.styleSettings?.colorThematicSettings?.enabled;

            if (isMouseoverEnabled && (!this._colorThematic || !isColorThematicEnabled)) {
                this._mouseoverLabelHandler = new AzureMouseoverLabelHandler(this.map, this);
            }

            this._onReady.resolve();
        });
    }

    private async createLayers() {
        await this._clusterLayer.create(this._datasource);
        await this._clusterLabelLayer.create(this._datasource);
    }

    public destroy() {
        super.destroy();
        this.map.events.remove('mouseover', this._clusterLayer.getAzureLayer() as any, this.onMouseOver);
        this._clusterLayer.remove();
        this._clusterLabelLayer.remove();
    }

    protected getColorThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    public getLayerIds(): string[] {
        return [this._clusterLayer.getLayerId(), this._clusterLabelLayer.getLayerId()];
    }

    protected getSymbolThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    public async update(_layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        await this._clusterLayer.update(this.layerView, this.layerInfo);
        await this._clusterLabelLayer.update(this.layerView, this.layerInfo);
    }

    protected hideFeature(_featureId: number): void {
        es.log.warn("Not implemented");
    }

    protected showFeatures(): void {
        es.log.warn("Not implemented");
    }

    public get clusterLabelLayer(): AzurePieClusterLabelLayer {
        return this._clusterLabelLayer;
    }

    public getActiveAzureLayerId(): string {
        return this._clusterLayer.getLayerId();
    }

    // todo: copy-pasted from point layer, should share logic
    public async getClusterLeavesFeats(clusterId: number): Promise<any[]> {
        return new Promise((resolve, _reject) => {

            if (this._datasource && this._datasource instanceof source.DataSource) {
                this._datasource.getClusterLeaves(clusterId, Infinity, 0).then((points) => {
                    const feats: data.Feature<data.Geometry, any>[] = [];
                    points.forEach(p => {
                        const pt = (p as data.Feature<data.Geometry, any>);
                        if (pt?.id) {
                            const id: number|undefined = !isNaN(pt.id) ? +pt.id : undefined;
                            if (id) {
                                feats.push(pt);
                            }
                        }
                    });
                    resolve(feats);
                }).catch(err => {
                    //es.log.warn("PieClusterLayer : error getting cluster leaves", err);
                    resolve([]);
                });
            } else {
                es.api.layerView.pbfGetClusterChildren(this.layerView.id, clusterId, {suppressDefaultErrorHandling: true}).then((feats: any) => {
                    resolve(feats.map((f: any) => GeomUtils.convertPbfFeatToGeoJsonFeat(f)));
                }).fail((_rs: any, jqXHR: any) => {
                    if(jqXHR?.responseText?.startsWith("SUPERCLUSTER_EXPIRED")) {
                        this.handleSuperclusterExpired();
                    }
                });
            }
        }) as Promise<any[]>;
    }

    /**
     * Low-level helper fn to render chart with generic data.
     * @param labels
     * @param data
     * @param colors
     * @param isAnimate
     * @private
     */
    private _drawPieChartWidget(labels: string[], data: number[], colors: string[], isAnimate = true) {

        // if symbol colour is dark then font and outline is dark, and vice-versa
        //const fontAndOutlineColor = luminance < 0.5 ? "rgba(255,255,255,1)" : "rgba(48,48,48,1)";
        const ctx = document.getElementById('_es_chart_pieChart').getContext('2d');

        // destroy the previous chart if it exists
        if (this._pieChartWidget) {
            this._pieChartWidget.destroy();
            this._pieChartWidget = null;
        }

        this.map.getCanvasContainer().style.cursor = 'default';

        this._pieChartWidget = new Chart(ctx, {
            type: 'pie',
            data: {
                labels: labels,
                datasets: [{
                    data: data,
                    backgroundColor: colors,
                    borderWidth: 0 // Remove borders for a cleaner smaller chart
                }]
            },
            options: {
                responsive: false,
                maintainAspectRatio: false,
                animation: {
                    duration: isAnimate ? 250 : 0
                },
                plugins: {
                    legend: {
                        display: false,
                        position: 'top'
                    },
                    tooltip: {
                        enabled: true
                    },
                    datalabels: {
                        display: true,
                        color: 'black',
                        formatter: (value, context) => {
                            if (value === 0) {
                                return '';
                            }
                            const label = context.chart.data.labels[context.dataIndex];
                            //const percentage = ((value / data.reduce((a, b) => a + b, 0)) * 100).toFixed(1);
                            //return `${label}: ${percentage}%`;
                            return `${label}`;
                        },
                        anchor: 'center',
                        align: 'center',
                        font: {
                            size: 10
                        }
                    }
                },
                layout: {
                    padding: 0
                },
                cutout: '0%'
            }
        } as ChartConfiguration);
    }

    private onMouseOver =(e: MapMouseEvent) => {

        const azLayer = this._clusterLabelLayer.getAzureLayer() as layer.BubbleLayer;

        if (azLayer?.getId() === e.layerId && e.shapes?.length > 0 && e.shapes[0]?.properties?.cluster) {
            this.map.getCanvasContainer().style.cursor = 'progress';

            if (this.hoverTimeoutId) {
                clearTimeout(this.hoverTimeoutId);
                this.hoverTimeoutId = undefined;
            }

            if(this._pieChartWidget){
                this.updateChart(e);
            } else {
                this.hoverTimeoutId = setTimeout(() => {
                    this.updateChart(e);
                    this.map.events.add('mousemove', azLayer, this.updateChart);
                    if (this.hoverTimeoutId) {
                        clearTimeout(this.hoverTimeoutId);
                        this.hoverTimeoutId = undefined;
                    }
                }, DELAY_TIMER) as unknown as number;
            }
        }
    }

    private updateChart = (evt: MapMouseEvent) => {
        this.map.getCanvasContainer().style.cursor = 'default';
        const clusterShape = evt?.shapes?.[0];

        if (clusterShape?.properties?.cluster) {

            const clusterId = parseInt(clusterShape.properties.cluster_id);

            // only redraw if the cluster id has changed
            if (this._pieChartWidget && this.currentClusterId === clusterId) {
                const chartDiv = document.getElementById('_es_chart_container');
                if (chartDiv?.style.display === 'block') {
                    chartDiv.style.left = (evt?.pixel?.[0] ?? 0) + 'px';
                    chartDiv.style.top = (evt?.pixel?.[1] ?? 0) + 'px';
                }
                //es.log.log("updateChart - just update location");
                return;
            }

            this.currentClusterId = clusterId;

            this.getClusterLeavesFeats(clusterId).then((pointFeats) => {
                if (pointFeats.length === 0) {
                    //es.log.log("updateChart : skip : pointFeats", pointFeats);
                    return;
                }

                const {labels, data, colors} = this.getLabelsDataColors(this._colorThematic, pointFeats, '#ffffff');
                if (data.length === 0) {
                    es.log.warn("charts data not parseable  thematic", this._colorThematic);
                    return;
                }

                const chartDiv = document.getElementById('_es_chart_container');
                chartDiv.style.display = 'block';
                chartDiv.style.left = (evt?.pixel[0] ?? 0) + 'px';
                chartDiv.style.top = (evt?.pixel[1] ?? 0) + 'px';

                this._drawPieChartWidget(labels, data, colors);
                //drawHorizontalBarChartWidget(labels, data, colors);
            });
        }
    }

    /**
     * Setup a ClusterChartLayer which is a layer that will show a circle and number of points for a cluster and, optionally, (if colorThematic enabled) a chart on cluster mouseover.
     */
    private setupNumberClusterLayer() {
        const layerView = this.layerView;

        if (!isPieClusteringOn(layerView) || !layerView.enabled || layerView.geomType !== "POINT") {
            es.log.log("setupNumberClusterLayer : skip", isPieClusteringOn(layerView), layerView.enabled, layerView.geomType, this._colorThematic);
            return;
        }

        const isMouseoverEnabled = isMouseoverLabellingEnabled(this.layerView);
        const isColorThematicEnabled = !!layerView.styleSettings?.colorThematicSettings?.enabled;

        es.log.log("setupNumberClusterLayer", this.layerView.title, this._colorThematic);

        // only set up the custom chart handler if we have color thematic,
        // otherwise, the AzureMouseoverLabelHandler will handle regular tooltips
        if (!this._colorThematic || !isMouseoverEnabled || !isColorThematicEnabled) {
            //es.log.log("setupNumberClusterLayer : no custom chart handler needed",
            //    {colorThematic: this._colorThematic, isMouseoverEnabled, isColorThematicEnabled}
            //);
            return;
        }

        const mapEvents = (this.map.events as any); // getting ide errs adding/removing events but code works -mismatched TS version?
        const azLayer = this._clusterLabelLayer.getAzureLayer();

        mapEvents.add('mouseover', azLayer, this.onMouseOver);

        const resetMouseMove = () => {
            //es.log.log("resetMouseMove");
            this.map.getCanvasContainer().style.cursor = 'default';
            const chartContainer = document.getElementById('_es_chart_container');
            chartContainer.style.display = 'none';
            this.currentClusterId = undefined;
            // destroy the previous chart if it exists
            if (this._pieChartWidget) {
                this._pieChartWidget.destroy();
                this._pieChartWidget = undefined;
            }
            if (this.hoverTimeoutId) {
                clearTimeout(this.hoverTimeoutId);
                this.hoverTimeoutId = undefined;
            }
            mapEvents.remove('mousemove', azLayer, this.updateChart);
        };

        mapEvents.add('mouseout', azLayer, resetMouseMove);
        mapEvents.add('mousedown', azLayer, resetMouseMove);
        mapEvents.add('wheel', azLayer, resetMouseMove);
    }

    private getLabelsDataColors(_colorThematic: any, pointFeats: any[], othersColor: string): {labels: string[], data: number[], colors:string[]} {
        const THRESHOLD_PERCENTAGE = 0.10; //  // must be at least 10% or pie slice is too small
        const MAX_SLICES = 8;

        const [ranges, rangeCounts] = this.getRangesAndCounts(_colorThematic, pointFeats);

        const labels: string[] = [];
        const data: number[] = [];
        const colors: string[] = [];

        let totalCount = 0;
        ranges.forEach((range, index) => {
            const count = rangeCounts[index];

            if (count > 0) {
                let label = "";
                if (_colorThematic.$type === "DistinctColorThematic") {
                    label = range.val === "" ? `${count}` : `${count}`; // use something like `${count} n/a` ?
                } else if (_colorThematic.$type === "RangeColorThematic" && !isNaN(parseFloat(range.val))) {
                    label = `${count}`;
                }
                labels.push(label);
                data.push(count);
                colors.push(es.util.color.serverColorToRGB(range.color));

                totalCount += count;
            }
        });

        const threshold = Math.round(totalCount * THRESHOLD_PERCENTAGE);

        let n = 0;
        for (let i=0; n<2 && i<data.length; i++) {
            if (data[i] < threshold) {
                n++;
            }
        }

        let othersCount = 0;
        if (n > 1) {
            for (let i = 0; i < data.length;) {
                if (data[i] < threshold) {
                    othersCount += data.splice(i, 1)[0];
                    labels.splice(i, 1);
                    colors.splice(i, 1);
                } else {
                    i++;
                }
            }
        }

        if (othersCount > 0) {
            labels.push(othersCount+'');
            data.push(othersCount);
            colors.push(othersColor);
        }

        while (data.length > MAX_SLICES) {
            if (othersCount == 0) {
                othersCount = 1; // just so it won't enter again
                labels.push('');
                data.push(0);
                colors.push(othersColor);
            }

            let min = -1;
            let minIndex = -1;
            for (let i=0; i<data.length-1; i++) {
                if (min == -1 || data[i] < min) {
                    min = data[i];
                    minIndex = i;
                }
            }

            data.splice(minIndex, 1);
            data[data.length - 1] += min;
            labels[labels.length - 1] = data[data.length - 1]+'';
            labels.splice(minIndex, 1);
            colors.splice(minIndex, 1);
        }

        return {labels, data, colors};
    }

    private getRangesAndCounts(_colorThematic: any, pointFeats: any[]): [any[], number[]] {

        const parseBoolean = (str) => {
            return str.toLowerCase() === "true";
        };

        const attrName = _colorThematic.attribute;
        const ranges: any[] = _colorThematic.ranges;
        const rangeCounts: number[] = new Array(ranges.length).fill(0);
        const isAttrBoolean = this.layerInfo.attributes.find(attr => attr.name === attrName).attrType === "BOOLEAN";

        if (Array.isArray(pointFeats) && pointFeats.length > 0) {
            // check if any feat has the artr. in case where none have it we will get a false-alarm log.warn
            const hasAttributeFeatures = pointFeats.some(feat =>
                feat?.properties && Object.prototype.hasOwnProperty.call(feat.properties, attrName)
            );
            if (!hasAttributeFeatures) {
                es.log.warn("getRangesAndCounts: No point features contain the required thematic attr", attrName, _colorThematic, pointFeats, isAttrBoolean);
            }
        }

        pointFeats.forEach(point => {
            const value = point.properties[attrName];
            const hasProperty = Object.prototype.hasOwnProperty.call(point.properties, attrName);

            for (let i = 0; i < ranges.length; i++) {
                if(_colorThematic.$type === "DistinctColorThematic"){
                    const rangeVal = ranges[i].val;

                    if (isAttrBoolean) {
                        if ((rangeVal === "True" && value === true) ||
                            (rangeVal === "False" && value === false) ||
                            (rangeVal === "" && !hasProperty)) {

                            rangeCounts[i]++;
                            break; // Only count once per feature
                        }
                    } else {
                        if (value === rangeVal ||
                            (rangeVal === "" && !hasProperty)) {

                            rangeCounts[i]++;
                            break; // Only count once per feature
                        }
                    }
                } else if(_colorThematic.$type === "RangeColorThematic") {
                    const rangeVal = parseFloat(ranges[i].val);
                    if (!isNaN(value)) {
                        if (i === ranges.length - 1) {
                            // If it's the last range, include values greater than the last range value
                            if (value > rangeVal) {
                                rangeCounts[i]++;
                                break;
                            }
                        } else if (value <= rangeVal) {
                            // For all other ranges, check if the value is less than or equal to the range value
                            rangeCounts[i]++;
                            break;
                        }
                    }
                } else {
                    es.log.warn("charts not supported yet for thematic", _colorThematic);
                }
            }
        });

        return [ranges, rangeCounts];
    }

    private async reloadSupercluster() {
        if (!this._datasource) {
            console.warn("reloadSupercluster called with no datasource for the layer.")
            return;
        }
        this.eventChannel.fireEvent("onShowLoadingOverlay");
        const datasourceFilter = this.getDatasourceFilter(this._colorThematic);
        await this.datasourceManager.recreate(this._datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);
        this.eventChannel.fireEvent("onHideLoadingOverlay");
    }

    public hide(): void {
        this._clusterLayer.hide();
        this._clusterLabelLayer.hide();
    }

    public show(): void {
        this._clusterLayer.show();
        this._clusterLabelLayer.show();
    }


}
